// Client for the Word Dash leaderboard API (Cloudflare Pages Function + D1).
//
// The API base is configurable via REACT_APP_LEADERBOARD_API (defaults to
// '/api/leaderboard', i.e. same-origin, which is how it is deployed on Pages).
import {
  MOCK_DISTRIBUTION,
  MOCK_LEADERBOARD,
  MOCK_PLAYER,
  MOCK_STATS,
} from './devMock'
import { authHeaders, getTokenName, hasToken } from './identity'

const API_BASE = process.env.REACT_APP_LEADERBOARD_API || '/api/leaderboard'

const PLAYER_KEY = 'leaderboardPlayerName'

export type LeaderboardEntry = {
  player: string
  guesses: number
  timeMs: number
  won: number
}

// Prefer the name baked into the signed token (authoritative identity); fall
// back to the locally stored free-text name for anonymous/dev play.
export const getPlayerName = (): string =>
  MOCK_STATS
    ? MOCK_PLAYER
    : getTokenName() || localStorage.getItem(PLAYER_KEY) || ''

export const setPlayerName = (name: string): void => {
  const clean = name.replace(/\s+/g, ' ').trim().slice(0, 40)
  localStorage.setItem(PLAYER_KEY, clean)
}

// Fetch the all-time ranked leaderboard (best result per player across all days).
export const fetchLeaderboard = async (
  limit = 100
): Promise<LeaderboardEntry[]> => {
  if (MOCK_STATS) return MOCK_LEADERBOARD
  const res = await fetch(
    `${API_BASE}?limit=${encodeURIComponent(limit)}`,
    // no-store: always hit the network so a just-submitted score is never
    // hidden by a previously-cached empty response.
    { headers: { accept: 'application/json' }, cache: 'no-store' }
  )
  if (!res.ok) {
    throw new Error(`Leaderboard fetch failed (${res.status})`)
  }
  const data = await res.json()
  return (data.scores as LeaderboardEntry[]) || []
}

// Fetch the global guess distribution across all players and all days.
// Returns a 7-element array: index 0 => solved in 1 guess … index 5 => 6,
// index 6 => ran out of guesses.
export const fetchDistribution = async (): Promise<number[]> => {
  if (MOCK_STATS) return MOCK_DISTRIBUTION
  const res = await fetch(`${API_BASE}?view=distribution`, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
  })
  if (!res.ok) {
    throw new Error(`Distribution fetch failed (${res.status})`)
  }
  const data = await res.json()
  const dist = (data.distribution as number[]) || []
  // Normalize to exactly 7 slots (an older 6-slot response reads as 0 losses).
  return [0, 1, 2, 3, 4, 5, 6].map((i) => Number(dist[i] || 0))
}

// Submit the completed game for the authenticated guest. The server reads the
// actual guesses/time/won from the guest's finished server-side progress row
// (write-once), so only { day } and the auth token are sent — the client
// cannot supply or improve the score. Returns the stored (possibly frozen)
// record, or throws on a hard error.
export const submitScore = async (params: { day: number }): Promise<void> => {
  // Without a token there is no authenticated identity to submit under.
  if (MOCK_STATS || !hasToken()) return
  const res = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ day: params.day }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Score submit failed (${res.status}): ${msg}`)
  }
}

// Format a millisecond solve time as m:ss (or s.s for sub-minute).
export const formatTime = (ms: number): string => {
  if (ms < 0) return '-'
  const totalSec = ms / 1000
  if (totalSec < 60) return `${totalSec.toFixed(1)}s`
  const m = Math.floor(totalSec / 60)
  const s = Math.round(totalSec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Ordinal string for a 1-based rank: 1 -> "1st", 2 -> "2nd", 46 -> "46th".
export const ordinal = (n: number): string => {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}
